import { Empty } from '@/components/common/empty';
import type { Disclosure } from '@/components/common/helpers';
import { StandardIcon } from '@/components/common/icon';
import { BasicModal, BasicModalFooter } from '@/components/common/modal';
import { PersonItem } from '@/components/common/output/person';
import { useApiWithAuth } from '@/hooks/api';
import { getFullName } from '@/shared/lib/names';
import { caseInsensitiveSort } from '@/lib/sort';
import type {
  HouseholdAndMemberData,
  LoadHouseholdResponse,
} from '@/store/people/actions/people';
import { getHousehold } from '@/store/people/actions/people';
import type { Person } from '@/store/people/types';
import { Avatar, ModalBody, ModalHeader } from '@heroui/react';
import { useEffect, useMemo, useRef, useState } from 'react';

export function HouseholdModal({
  person,
  disclosure,
}: {
  person: Person | null;
  disclosure: Disclosure;
}) {
  const { loading, data, clear } = useLoadHousehold(person);

  return (
    <BasicModal
      disclosure={disclosure}
      ready={Boolean(person)}
      loading={loading}
      onClose={clear}
    >
      <ModalHeader>View Household</ModalHeader>
      <ModalBody>
        {data && !data.exists && (
          <Empty description='Household not found' icon='mdi:alert' />
        )}
        {data?.exists && <HouseholdDetails data={data} />}
      </ModalBody>
      <BasicModalFooter
        showApply={false}
        cancelLabel='Close'
        onCancel={disclosure.onClose}
      />
    </BasicModal>
  );
}

function HouseholdDetails({ data }: { data: HouseholdAndMemberData }) {
  const PersonDetails = ({ person }: { person: Person }) => {
    if (!person) {
      return undefined;
    }

    let nameEndContent;
    if (data.household.data.head === person.id) {
      nameEndContent = (
        <StandardIcon id='mdi:cog-outline' className='text-default-400' />
      );
    } else if (data.household.data.managers?.includes(person.id)) {
      nameEndContent = (
        <StandardIcon id='mdi:pencil-outline' className='text-default-400' />
      );
    }

    return <PersonItem item={person} icons nameEndContent={nameEndContent} />;
  };

  const members = useHouseholdMembersList(data);

  return (
    <>
      <div className='flex flex-row items-center gap-2'>
        <Avatar
          size='sm'
          className='bg-default-200'
          showFallback
          fallback={
            <StandardIcon
              id='mdi:house-outline'
              size={22}
              className='text-default-600'
            />
          }
        />
        <span className='text-medium'>{data.household.data.name}</span>
      </div>
      <div className='flex w-full flex-col items-start justify-center gap-2'>
        {members.map((person) => (
          <PersonDetails key={person.id} person={person} />
        ))}
      </div>
    </>
  );
}

function useLoadHousehold(person: Person | null) {
  const [loading, setLoading] = useState(false);
  const lastId = useRef('');
  const [data, setData] = useState<LoadHouseholdResponse | null>(null);
  const { api, auth } = useApiWithAuth();
  useEffect(() => {
    if (
      !person?.data.household_id ||
      lastId.current === person.data.household_id
    ) {
      return;
    }

    lastId.current = person.data.household_id;

    setLoading(true);
    getHousehold({ id: person.data.household_id, api, auth })
      .then((r) => {
        setData(r);
        setLoading(false);

        return;
      })
      .catch((e) => {
        console.error(e);
        setLoading(false);
      });
  }, [person, api, auth]);
  const clear = () => {
    debugger;
    lastId.current = '';
    setData(null);
  };

  return { loading, data, clear };
}

function useHouseholdMembersList(data: HouseholdAndMemberData) {
  const members = useMemo(() => {
    const comparator = caseInsensitiveSort((item: Person) =>
      getFullName(item.data),
    );

    return data.household.data.members
      .map((id) => data.people[id])
      .filter((person) => Boolean(person))
      .sort(comparator);
  }, [data]);

  return members;
}
